import { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import PowerWaveBackground from '../experiments/PowerWaveBackground';
import StatusPill from '../common/StatusPill';
import { publicAPI } from '../../services/api';
import { getPlatformVersion, mergeWithLiveVersion } from '../../utils/platformVersion';

/**
 * LoginBrandPanel — the dark-navy "alive" brand pane that sits on the
 * left side of the auth split. Stacks layers, back-to-front:
 *
 *   1. Deep navy gradient base.
 *   2. Soft floating glowing orbs (CSS radial gradients drifting on a
 *      slow keyframe — purely decorative, hidden under reduced-motion).
 *   3. Subtle dot grid (CSS background) drifting horizontally.
 *   4. The Power Calculator's harmonic sine-wave layer (retinted teal +
 *      emerald), filling the whole brand panel so the wave reads as a
 *      continuous field running top-to-bottom of the pane.
 *   5. A vignette.
 *   6. The macOS-grade frosted-glass HERO STRIP — full-width, square
 *      sides, hairline top/bottom borders. The strip is heavily blurred
 *      but only lightly washed (rgba ≤ 0.06), so the wave field is
 *      visibly bending THROUGH the glass. The wave continues clear above
 *      and below the strip, making it read as a glass slab cut through a
 *      single continuous wave field rather than a panel sitting on top of
 *      a separate background.
 *   7. The strip's content: HD logo (floating directly on the glass for
 *      the full Apple effect), wordmark, tagline, uptime stat badge.
 *   8. Corner chrome — LIVE chip (top-right) and version eyebrow
 *      (bottom-left), pinned to the panel edges, sitting above the strip.
 *
 * Live stat: a single uptime badge sourced from
 * `GET /api/public/platform-stats` (30-day SLO via `STATUS_UPTIME_30D`).
 * We intentionally avoid rotating experiment/event counters here — the
 * platform is upgraded periodically and those figures would read stale.
 *
 * All motion is reduced-motion safe; the pane degrades to a static
 * gradient + static badges. The frosted-glass strip itself is static —
 * the user-supplied glass tokens (`backdrop-filter: blur(28px)
 * saturate(1.4)`) carry the Apple vibe without animation. The strip
 * falls back to its translucent wash on browsers without
 * `backdrop-filter` support so the surface still reads as glass.
 */

const REFRESH_INTERVAL_MS = 60_000;

function formatPercent(value, digits = 0) {
  if (value == null || !Number.isFinite(Number(value))) return null;
  return `${(Number(value) * 100).toFixed(digits).replace(/\.0$/, '')}%`;
}

const DEFAULT_UPTIME_30D = 0.9997;

const FALLBACK_UPTIME_STAT = {
  id: 'uptime',
  label: `${formatPercent(DEFAULT_UPTIME_30D, 2)} UPTIME · 30D`,
  tone: 'navy',
};

function formatTimestampShort(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  } catch (_) {
    return null;
  }
}

/**
 * Uptime badge from `/platform-stats` — the only login stat we surface.
 */
function buildUptimeStat(stats) {
  const uptime = stats?.uptime30d;
  if (uptime == null || !Number.isFinite(Number(uptime))) return null;
  const label = formatPercent(uptime, 2);
  if (!label) return null;
  return {
    id: 'uptime',
    label: `${label} UPTIME · 30D`,
    tone: 'navy',
  };
}

export default function LoginBrandPanel({ tagline = 'Make every visit count.' }) {
  const [reducedMotion, setReducedMotion] = useState(false);
  const [liveStats, setLiveStats] = useState(null);
  const [statsFetchAt, setStatsFetchAt] = useState(null);
  const cancelledRef = useRef(false);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const apply = () => setReducedMotion(mq.matches);
    apply();
    if (mq.addEventListener) mq.addEventListener('change', apply);
    else if (mq.addListener) mq.addListener(apply);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', apply);
      else if (mq.removeListener) mq.removeListener(apply);
    };
  }, []);

  // Mount-fetch + 60s polite revalidation against the cached
  // `/api/public/platform-stats` endpoint. The server caches by
  // date-minute so this loop never thunders the database.
  useEffect(() => {
    cancelledRef.current = false;
    let timerId = null;

    const load = async () => {
      try {
        const res = await publicAPI.getPlatformStats();
        if (cancelledRef.current) return;
        const data = res?.data?.data || null;
        if (data) {
          setLiveStats(data);
          setStatsFetchAt(new Date().toISOString());
        }
      } catch (_) {
        // Swallow — fallback uptime keeps the panel readable; retry quietly.
      }
    };

    load();
    timerId = window.setInterval(load, REFRESH_INTERVAL_MS);

    return () => {
      cancelledRef.current = true;
      if (timerId) window.clearInterval(timerId);
    };
  }, []);

  const uptimeStat = useMemo(
    () => buildUptimeStat(liveStats) || FALLBACK_UPTIME_STAT,
    [liveStats]
  );
  const isLive = liveStats?.uptime30d != null;

  const liveTimestampLabel = formatTimestampShort(liveStats?.updatedAt || statsFetchAt);
  const liveChipLabel = liveTimestampLabel ? `LIVE · ${liveTimestampLabel}` : 'LIVE';
  const platform = getPlatformVersion();
  const versionString = mergeWithLiveVersion(liveStats);
  const eyebrow = `${platform.name} · v${versionString}`;

  return (
    <aside className="txnweb-login-brand" aria-label="txnweb.com — Website Tracking">
      <div className="txnweb-login-brand-orbs" aria-hidden>
        <span className="txnweb-login-orb txnweb-login-orb--cyan" />
        <span className="txnweb-login-orb txnweb-login-orb--emerald" />
        <span className="txnweb-login-orb txnweb-login-orb--violet" />
        <span className="txnweb-login-orb txnweb-login-orb--amber" />
      </div>
      <div className="txnweb-login-brand-grid" aria-hidden />
      <div className="txnweb-login-brand-wave" aria-hidden>
        <PowerWaveBackground
          tone="teal"
          amplitude={26}
          frequency={3}
          durationMs={14000}
        />
      </div>
      <div className="txnweb-login-brand-vignette" aria-hidden />

      {/* macOS-grade frosted-glass hero strip — full-width band cut
          through the wave field, with the brand content centred inside.
          The HD logo floats directly on the glass for the Apple feel
          described in the design brief. */}
      <div className="txnweb-login-brand-strip">
        <div className="txnweb-login-brand-content">
          <img
            src="/txnweb-logo.png"
            alt="txnweb.com — Website Tracking"
            className="txnweb-login-brand-logo"
            width={1024}
            height={682}
            decoding="async"
          />
          <h1 className="txnweb-login-brand-wordmark">txnweb.com</h1>
          <p className="txnweb-login-brand-strapline">Website Tracking</p>
          <p className="txnweb-login-brand-tagline">{tagline}</p>

          <div
            className={`txnweb-login-brand-stats${reducedMotion ? ' is-static' : ''}`}
            aria-live="polite"
            aria-atomic="true"
          >
            <StatusPill
              status={{
                id: 'succeeded',
                label: uptimeStat.label,
                tone: uptimeStat.tone,
              }}
              onDark
            />
          </div>
        </div>
      </div>

      {isLive && (
        <div className="txnweb-login-brand-live-chip" aria-hidden>
          <StatusPill
            status={{ id: 'running', label: liveChipLabel, tone: 'sky' }}
            onDark
            compact
          />
        </div>
      )}

      <div className="txnweb-login-brand-eyebrow" aria-hidden>
        {eyebrow}
      </div>
    </aside>
  );
}

LoginBrandPanel.propTypes = {
  tagline: PropTypes.string,
};
